import { useState, useEffect, useRef } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { httpsCallable } from 'firebase/functions'
import { useAuthState, logout, switchOrganization } from '@/hooks/useAuth'
import { getUserActiveOrgId, getUserActiveOrganizations, getUserAccessLevel, getUserRoleInOrg } from '@/lib/multiOrgHelpers'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { db, functions } from '@/lib/firebase'
import { useOrgConfig } from '@/hooks/useOrgConfig'
import NavDropdownMenu, { MobileNav } from '@/components/ui/NavDropdownMenu'
import type { NavDropdownMenuProps } from '@/components/ui/NavDropdownMenu'
import type { MyVerslagTodo } from '@/types'

export default function Layout() {
  const { user, userProfile } = useAuthState()
  const navigate = useNavigate()
  const [orgNames, setOrgNames] = useState<Record<string, string>>({})
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  const accessLevel = getUserAccessLevel(userProfile)
  const isSchoolOrGroupAdmin = ['platform-admin', 'group-admin', 'school-admin'].includes(accessLevel)
  const canManageNascholing = ['platform-admin', 'group-admin', 'school-admin', 'nascholingsbeheer'].includes(accessLevel)
  const canManageGebruikers = ['platform-admin', 'group-admin', 'school-admin', 'gebruikersbeheer'].includes(accessLevel)
  const isAdmin = accessLevel === 'platform-admin'
  const activeOrgId = getUserActiveOrgId(userProfile)
  const hasDirectieAccess = userProfile?.directieAccess === true
  const hasAvgInfoveiligheidsbeheerderAccess = (userProfile?.avgInfoveiligheidsbeheerderVoor?.length ?? 0) > 0

  const {
    snapScanEnabled,
    snapScanDirectieEnabled,
    tempeltoolEnabled,
    lesbezoekenEnabled,
    schoolleiderEvaluatieEnabled,
    verslagenEnabled,
    go2030Enabled,
    videoObpEnabled,
    dienstbrievenEnabled,
    sopEnabled,
    swpEnabled,
    mijnABCEnabled,
    mijnVakdossierEnabled,
    selfAssessmentsEnabled,
    leerlingenaantallenEnabled,
    leerlingenstromenEnabled,
    leerlingenherkomstEnabled,
    regionaalAanbodEnabled,
    avgRegisterEnabled,
    bvhEnabled,
    gevolgdeNascholingenTeamOverzichtEnabled,
  } = useOrgConfig()

  // Open to-do telling voor badge in navigatie
  const { data: openTodoCount = 0 } = useQuery({
    queryKey: ['openVerslagTodoCount', activeOrgId],
    queryFn: async () => {
      const fn = httpsCallable(functions, 'getMyVerslagTodos')
      const result = await fn({ orgId: activeOrgId })
      const todos = (result.data as any).data as MyVerslagTodo[]
      const uid = userProfile?.uid || ''
      return todos.filter(t => {
        if (t.status === 'afgerond') return false
        if (t.iedereen && t.afgevinktDoor?.includes(uid)) return false
        return true
      }).length
    },
    enabled: !!activeOrgId && verslagenEnabled,
    staleTime: 2 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  })

  // Openstaande nascholingsaanvragen-telling voor badge op "Professionalisering" (en
  // bubbelt door naar "Mijn school"). Hergebruikt 2 bestaande callables, geen nieuwe
  // backend-functie: standaardproces-aanvragen tellen altijd mee (elke beheerder met
  // rechten kan ze oppakken); flow-aanvragen enkel als de kijker zelf nog een
  // wachtende stap heeft (via approvalFlows.stappen[].gebruikerIds) — niet elke
  // flow-aanvraag voor iedereen.
  const { data: pendingAanvragenCount = 0 } = useQuery({
    queryKey: ['pendingAanvragenCount', activeOrgId, userProfile?.uid],
    queryFn: async () => {
      const reqFn = httpsCallable(functions, 'getPendingTrainingRequests')
      const reqResult = await reqFn({ orgId: activeOrgId })
      const requests = (reqResult.data as any).requests as any[]
      if (requests.length === 0) return 0

      const nietFlow = requests.filter(r => !r.flowActief)
      const flowActief = requests.filter(r => r.flowActief)
      if (flowActief.length === 0) return nietFlow.length

      const flowFn = httpsCallable(functions, 'getApprovalFlow')
      const flowResult = await flowFn({ orgId: activeOrgId })
      const stappen = ((flowResult.data as any)?.flow?.stappen ?? []) as { id: string; gebruikerIds: string[] }[]
      const uid = userProfile?.uid || ''
      const mijnStapIds = new Set(stappen.filter(s => s.gebruikerIds.includes(uid)).map(s => s.id))

      const relevanteFlow = flowActief.filter(r =>
        ((r as any).goedkeuringsStappen ?? []).some((s: any) => mijnStapIds.has(s.stapId) && s.status === 'wachtend')
      )
      return nietFlow.length + relevanteFlow.length
    },
    enabled: !!activeOrgId && canManageNascholing,
    staleTime: 2 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  })

  // Haal organisatie namen op voor multi-org dropdown
  useEffect(() => {
    if (!userProfile?.organizations) return

    const fetchOrgNames = async () => {
      const orgIds = userProfile.organizations!.map(o => o.orgId)
      const names: Record<string, string> = {}

      for (const orgId of orgIds) {
        const q = query(collection(db, 'organizations'), where('orgId', '==', orgId))
        const snap = await getDocs(q)
        if (!snap.empty) {
          names[orgId] = snap.docs[0].data().naam
        }
      }

      setOrgNames(names)
    }

    fetchOrgNames()
  }, [userProfile?.organizations])

  // Sluit user menu bij klik buiten dropdown
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    if (userMenuOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [userMenuOpen])

  async function handleLogout() {
    await logout()
    navigate('/login')
  }

  async function handleSwitchOrg(orgId: string) {
    try {
      await switchOrganization(orgId)
    } catch (error) {
      console.error('Error switching organization:', error)
      alert('Fout bij wisselen van organisatie')
    }
  }

  const activeOrganizations = getUserActiveOrganizations(userProfile)
  const hasMultipleOrgs = activeOrganizations.length > 1

  // ── Menu-structuur (7 groepen) ──────────────────────────────────────────

  const menuGroups: NavDropdownMenuProps[] = [
    {
      label: 'Mijn BSA',
      hubPath: '/mijn-bsa',
      items: [
        { label: 'SnapScan', href: '/snapscan', visible: snapScanEnabled },
        { label: 'SnapScan schoolleiders', href: '/snapscan-directie', visible: hasDirectieAccess && snapScanDirectieEnabled },
        { label: 'Tempeltool', href: 'https://bvv.scholengemeenschap.gent', external: true, visible: tempeltoolEnabled },
        { label: 'Mijn GO!2030', href: '/go2030', visible: go2030Enabled && isSchoolOrGroupAdmin },
        { label: 'Leerlingenaantallen', href: '/leerlingenaantallen', visible: leerlingenaantallenEnabled && isSchoolOrGroupAdmin },
        { label: 'Leerlingenstromen', href: '/leerlingenstromen', visible: leerlingenstromenEnabled && isSchoolOrGroupAdmin },
        { label: 'Leerlingenherkomst', href: '/leerlingenherkomst', visible: leerlingenherkomstEnabled && isSchoolOrGroupAdmin },
        { label: 'Regionaal studieaanbod', href: '/regionaal-aanbod', visible: regionaalAanbodEnabled && isSchoolOrGroupAdmin },
      ],
    },
    {
      label: 'Mijn professionalisering',
      hubPath: '/mijn-professionalisering',
      items: [
        { label: 'Nascholingsaanbod', href: '/nascholingen', visible: true },
        { label: 'Mijn aanvragen', href: '/mijn-aanvragen', visible: true },
        { label: 'Mijn nascholingen', href: '/mijn-nascholingen', visible: true },
        { label: 'Mijn certificaten', href: '/mijn-certificaten', visible: true },
        { label: 'Mijn OLP-checks', href: '/self-assessments?type=olp', visible: selfAssessmentsEnabled },
        { label: 'Mijn evaluatie-checks', href: '/self-assessments?type=evaluatie10', visible: selfAssessmentsEnabled },
        { label: 'Mijn PROEV-zelfscan', href: '/self-assessments?type=proev-zelfscan', visible: selfAssessmentsEnabled },
      ],
    },
    {
      label: 'Mijn school',
      hubPath: '/mijn-school',
      items: [
        { label: 'Mijn platform', href: '/mijn-platform', visible: isSchoolOrGroupAdmin },
        { label: 'Mijn schooldata', href: '/rapportage', visible: isSchoolOrGroupAdmin },
        { label: 'Mijn verslagen', href: '/verslagen', visible: verslagenEnabled, badge: openTodoCount },
        { label: 'Mijn dienstbrieven', href: '/dienstbrieven', visible: dienstbrievenEnabled },
        { label: 'Mijn SOP', href: '/sop', visible: sopEnabled && isSchoolOrGroupAdmin },
        { label: 'Mijn SWP', href: '/swp', visible: swpEnabled && isSchoolOrGroupAdmin },
        { label: 'Mijn vakdossier', href: '/mijn-vakdossier', visible: mijnVakdossierEnabled },
        { label: 'Mijn ABC', href: '/mijn-abc', visible: mijnABCEnabled },
        { label: 'Mijn AVG', href: '/avg-register', visible: avgRegisterEnabled && (isSchoolOrGroupAdmin || hasAvgInfoveiligheidsbeheerderAccess) },
        { label: 'Mijn BVH', href: '/mijn-bvh', visible: bvhEnabled },
        { label: 'Mijn lesbezoeken', href: '/lesbezoeken', visible: lesbezoekenEnabled },
        { label: 'Mijn schoolleiders', href: '/schoolleider-evaluatie', visible: schoolleiderEvaluatieEnabled && hasDirectieAccess },
        { label: 'Video-OBP', href: 'https://video-obp.scholengemeenschap.gent/', external: true, visible: videoObpEnabled },
        { label: 'Professionalisering', href: '/school-professionalisering', visible: canManageNascholing, badge: pendingAanvragenCount },
        { label: 'Gebruikersbeheer', href: '/school-gebruikersbeheer', visible: canManageGebruikers && !isSchoolOrGroupAdmin },
        { label: 'Datalek melden', href: '/datalek-melden', visible: avgRegisterEnabled },
      ],
    },
    {
      label: 'Admin',
      hubPath: '/admin',
      items: [
        { label: 'Organisaties', href: '/admin?tab=organisaties', visible: isAdmin },
        { label: 'Gebruikers', href: '/admin?tab=gebruikers', visible: isAdmin },
        { label: 'Rollen', href: '/admin?tab=rollen', visible: isAdmin },
        { label: 'Accounttypes', href: '/admin?tab=accounttypes', visible: isAdmin },
        { label: 'Professionalisering', href: '/admin?tab=professionalisering', visible: isAdmin },
        { label: 'BSA', href: '/admin?tab=bsa', visible: isAdmin },
        { label: 'Leerplannen', href: '/admin?tab=leerplannen', visible: isAdmin },
        { label: 'Platformgebruik', href: '/admin?tab=platformgebruik', visible: isAdmin },
      ],
    },
  ]

  // Mijn GO!To voor mobiel: apart behandeld
  const mobileGoToGroup: NavDropdownMenuProps | null =
    (snapScanEnabled || gevolgdeNascholingenTeamOverzichtEnabled) && hasDirectieAccess && snapScanDirectieEnabled
      ? {
          label: 'Mijn GO!To',
          hubPath: '/mijn-goto',
          items: [
            { label: 'Mijn GO!To professionals', href: '/snapscan-analyse', visible: true },
            { label: 'Mijn GO!To schoolleiders', href: '/snapscan-directie-analyse', visible: true },
          ],
        }
      : null

  // Voor mobiel: voeg GO!To groep in na Mijn BSA (index 0)
  const mobileGroups = mobileGoToGroup
    ? [menuGroups[0], mobileGoToGroup, ...menuGroups.slice(1)]
    : menuGroups

  const plainLinks = [
    { label: 'Home', href: '/', visible: true },
    // Mijn GO!To als directe link voor niet-directie users
    { label: 'Mijn GO!To professionals', href: '/snapscan-analyse', visible: (snapScanEnabled || gevolgdeNascholingenTeamOverzichtEnabled) && !(hasDirectieAccess && snapScanDirectieEnabled) },
    { label: 'Mijn profiel', href: '/profiel', visible: true },
  ]

  return (
    <div className="min-h-screen flex flex-col">
      <nav className="bg-primary text-primary-foreground px-6 py-4 flex items-center justify-between shadow">
        <div className="flex items-center gap-8">
          <div className="leading-tight">
            <div className="font-bold text-lg">iGO! Professional</div>
            <div className="text-xs text-primary-foreground/80">Learning Hub</div>
          </div>

          {/* Desktop navigatie */}
          <div className="hidden md:flex gap-6 text-sm items-center">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                isActive ? 'text-white font-medium' : 'text-primary-foreground/70 hover:text-white transition-colors'
              }
            >
              Home
            </NavLink>

            {menuGroups.slice(0, 1).map(group => (
              <NavDropdownMenu key={group.label} {...group} />
            ))}

            {/* Mijn GO!To: speciaal gedrag afhankelijk van directie-toegang. Ook
                bereikbaar via enkel de teamzichtbaarheid-toggle, los van SnapScan. */}
            {(snapScanEnabled || gevolgdeNascholingenTeamOverzichtEnabled) && (
              hasDirectieAccess && snapScanDirectieEnabled ? (
                <NavDropdownMenu
                  label="Mijn GO!To"
                  hubPath="/mijn-goto"
                  items={[
                    { label: 'Mijn GO!To professionals', href: '/snapscan-analyse', visible: true },
                    { label: 'Mijn GO!To schoolleiders', href: '/snapscan-directie-analyse', visible: true },
                  ]}
                />
              ) : (
                <NavLink
                  to="/snapscan-analyse"
                  className={({ isActive }) =>
                    isActive ? 'text-white font-medium' : 'text-primary-foreground/70 hover:text-white transition-colors'
                  }
                >
                  Mijn GO!To professionals
                </NavLink>
              )
            )}

            {menuGroups.slice(1).map(group => (
              <NavDropdownMenu key={group.label} {...group} />
            ))}
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden text-white text-xl p-1"
            onClick={() => setMobileMenuOpen(true)}
          >
            ☰
          </button>
        </div>

        <div className="flex items-center gap-4 text-sm">
          {/* Organisatie switcher */}
          {hasMultipleOrgs && (
            <select
              value={userProfile?.activeOrgId || ''}
              onChange={(e) => handleSwitchOrg(e.target.value)}
              className="bg-primary-foreground/10 text-white border border-primary-foreground/20 rounded px-3 py-1.5 text-sm hover:bg-primary-foreground/20 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-foreground/30"
              title="Wissel van organisatie"
            >
              {activeOrganizations.map(org => {
                const roleMap: Record<string, string> = {
                  'platform-admin': 'Platform Admin',
                  'group-admin': 'Groep Admin',
                  'school-admin': 'School Admin',
                  'nascholingsbeheer': 'Nascholingsbeheer',
                  'preventieadviseur': 'Preventieadviseur',
                  'gebruikersbeheer': 'Gebruikersbeheerder',
                  'issuer': 'Uitgever',
                  'user': 'Gebruiker'
                }
                // getUserRoleInOrg i.p.v. het rauwe org.role: een globale (legacy
                // toegangsniveau) platform-admin krijgt overal die rechten, ongeacht
                // de specifieke per-org-rol — de weergave moet dat ook tonen, anders
                // lijkt het alsof die rol "teruggeschroefd" is naar gewone gebruiker.
                const effectieveRol = getUserRoleInOrg(userProfile, org.orgId)
                return (
                  <option key={org.orgId} value={org.orgId} className="bg-white text-gray-900">
                    {orgNames[org.orgId] || org.orgId} ({roleMap[effectieveRol]})
                  </option>
                )
              })}
            </select>
          )}

          {/* User menu dropdown */}
          <div className="relative hidden md:block" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="text-primary-foreground/70 hover:text-white transition-colors flex items-center gap-1"
            >
              {userProfile?.naam || user?.email}
              <span className="text-[10px] opacity-60">▾</span>
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[160px] z-50">
                <NavLink
                  to="/profiel"
                  onClick={() => setUserMenuOpen(false)}
                  className={({ isActive }) =>
                    `block px-4 py-2 text-sm transition-colors ${
                      isActive ? 'bg-primary/10 text-primary font-medium' : 'text-gray-700 hover:bg-gray-50'
                    }`
                  }
                >
                  Mijn profiel
                </NavLink>
                <div className="border-t border-gray-100 my-1" />
                <button
                  onClick={() => { setUserMenuOpen(false); handleLogout() }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Afmelden
                </button>
              </div>
            )}
          </div>

          {/* Mobile: alleen afmelden knop */}
          <button
            onClick={handleLogout}
            className="md:hidden text-primary-foreground/70 hover:text-white transition-colors text-sm"
          >
            Afmelden
          </button>
        </div>
      </nav>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <MobileNav
          groups={mobileGroups}
          plainLinks={plainLinks}
          onClose={() => setMobileMenuOpen(false)}
        />
      )}

      <main className="flex-1 container mx-auto px-6 py-8 max-w-6xl">
        <Outlet />
      </main>

      <footer className="text-center py-4 text-sm text-muted-foreground border-t space-y-1">
        <div>iGO! Professional - Learning Hub - GO! scholengroep Gent</div>
        <div>
          &copy; {new Date().getFullYear()} - all rights reserved -{' '}
          <a href="mailto:bob.podevijn@gmail.com" className="hover:underline">bp</a>
        </div>
      </footer>
    </div>
  )
}
